import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import s from './TimeSelect.module.scss';

/** Row height in px. `.wheelItem` is drawn at the same height. */
const ROW = 36;
/** A looping column renders this many copies of its values. Every stop jumps back
    to the same row in the middle copy, so a flick never reaches an end. */
const COPIES = 21;
const MIDDLE = Math.floor(COPIES / 2);
/** iOS keeps firing scroll through momentum and the snap, so a quiet gap is the stop. */
const SETTLE_MS = 140;
/** What a wheel shows before it has a value. */
const FALLBACK = new Date(2000, 0, 1, 9, 0);

type Column = { label: string; values: string[]; loop: boolean };

const columnsFor = (step: number): Column[] => [
  { label: 'Hour', values: Array.from({ length: 12 }, (_, i) => String(i + 1)), loop: true },
  {
    label: 'Minute',
    values: Array.from({ length: Math.floor(60 / step) }, (_, i) =>
      String(i * step).padStart(2, '0'),
    ),
    loop: true,
  },
  { label: 'AM or PM', values: ['AM', 'PM'], loop: false },
];

/** The value index each column shows for a time. Minutes floor to the step. */
const indexesOf = (d: Date, step: number) => [
  (d.getHours() % 12 || 12) - 1,
  Math.floor(d.getMinutes() / step),
  d.getHours() >= 12 ? 1 : 0,
];

/** Where a value index rests: the middle copy for a looping column. */
const restRow = (col: Column, index: number) => (col.loop ? MIDDLE * col.values.length : 0) + index;

const indexAt = (el: HTMLElement, col: Column) => {
  const len = col.values.length;
  return ((Math.round(el.scrollTop / ROW) % len) + len) % len;
};

/** How many times the hour passed between 11 and 12 rolling from one row to another. */
const noonCrossings = (from: number, to: number) => {
  let n = 0;
  for (let row = Math.min(from, to) + 1; row <= Math.max(from, to); row += 1) {
    if (row % 12 === 11) n += 1;
  }
  return n;
};

const behaviour = (): ScrollBehavior =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

/** The drum: rows tilt and fade with their distance from the centre row. */
const paint = (el: HTMLElement) => {
  const centre = el.scrollTop / ROW;
  const from = Math.max(0, Math.floor(centre) - 4);
  const to = Math.min(el.children.length - 1, Math.ceil(centre) + 4);
  for (let i = from; i <= to; i += 1) {
    const d = i - centre;
    const item = el.children[i] as HTMLElement;
    item.style.transform = `perspective(320px) rotateX(${-d * 22}deg)`;
    item.style.opacity = String(Math.max(0.2, 1 - Math.abs(d) * 0.28));
  }
};

/**
 * **A time on an iPhone-style wheel.** Hour, minute and AM/PM, each a native
 * scroll-snap column, so a flick gets the platform's own momentum. Hour and
 * minute loop: 11 rolls on to 12 and 1, 55 rolls on to 00.
 *
 * It commits only when a column comes to rest, never mid-flick, so a parent
 * re-render cannot fight the momentum. A value the wheel already shows does
 * not move it.
 */
export const TimeWheel = ({
  value,
  onChange,
  step = 5,
  label,
  className,
}: {
  value: Date | null;
  onChange: (next: Date) => void;
  step?: number;
  label?: string;
  className?: string;
}) => {
  const columns = useMemo(() => columnsFor(step), [step]);
  const cols = useRef<(HTMLDivElement | null)[]>([]);
  const timers = useRef<number[]>([]);
  /* A finger on the glass: a pause mid-drag is not a stop. */
  const held = useRef(false);
  /* A mouse drag, so the click that ends it does not also pick a row. */
  const drag = useRef<{ moved: boolean } | null>(null);
  /* Where each column was last put by the value, so that a scroll event we
     caused ourselves is not read back as a choice. */
  const placed = useRef<number[]>([]);
  const latest = useRef({ value, onChange });

  useLayoutEffect(() => {
    latest.current = { value, onChange };
  });

  useLayoutEffect(() => {
    const wanted = indexesOf(value ?? FALLBACK, step);
    columns.forEach((col, c) => {
      const el = cols.current[c];
      if (!el || timers.current[c]) return;
      const top = restRow(col, wanted[c]) * ROW;
      if (el.scrollTop !== top) el.scrollTop = top;
      placed.current[c] = top;
      paint(el);
    });
  }, [value, step, columns]);

  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);

  const commit = (flip: boolean) => {
    const [h, m, ap] = columns.map((col, c) => {
      const el = cols.current[c];
      return el ? indexAt(el, col) : 0;
    });
    const { value: current, onChange: emit } = latest.current;
    const next = new Date(current ?? new Date());
    const pm = flip ? ap === 0 : ap === 1;
    next.setHours(((h + 1) % 12) + (pm ? 12 : 0), m * step, 0, 0);
    if (current && next.getTime() === current.getTime()) return;
    emit(next);
  };

  const settle = (c: number) => {
    timers.current[c] = 0;
    const el = cols.current[c];
    if (!el || !el.isConnected || held.current || drag.current) return;
    const col = columns[c];
    const row = Math.round(el.scrollTop / ROW);
    /* Off a row only after a mouse drag, which runs with snap off. */
    if (Math.abs(el.scrollTop - row * ROW) > 1) {
      el.scrollTo({ top: row * ROW, behavior: behaviour() });
      return;
    }
    /* Rolling the hour through 11 and 12 turns AM to PM, as on an iPhone:
       two hours on from 11 AM is 1 PM, not 1 AM. */
    const flip = c === 0 && noonCrossings(Math.round(placed.current[c] / ROW), row) % 2 === 1;
    const rest = restRow(col, indexAt(el, col)) * ROW;
    if (el.scrollTop !== rest) el.scrollTop = rest;
    paint(el);
    if (placed.current[c] === rest && !flip) return;
    placed.current[c] = rest;
    commit(flip);
  };

  const wait = (c: number) => {
    clearTimeout(timers.current[c]);
    timers.current[c] = window.setTimeout(() => settle(c), SETTLE_MS);
  };

  const onScroll = (c: number) => {
    const el = cols.current[c];
    if (!el) return;
    paint(el);
    wait(c);
  };

  /* Desktop has no flick, so a mouse drags the column by hand. Snap is off for
     the drag, or the browser would fight every pixel of it. */
  const startDrag = (c: number) => (e: React.PointerEvent) => {
    const el = cols.current[c];
    if (!el || e.pointerType !== 'mouse' || e.button !== 0) return;
    const y = e.clientY;
    const top = el.scrollTop;
    const state = { moved: false };
    drag.current = state;
    el.style.scrollSnapType = 'none';
    const move = (ev: PointerEvent) => {
      if (Math.abs(ev.clientY - y) > 3) state.moved = true;
      el.scrollTop = top - (ev.clientY - y);
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      el.style.scrollSnapType = '';
      /* Cleared after the click this pointerup is about to fire. */
      window.setTimeout(() => {
        drag.current = null;
        wait(c);
      }, 0);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  };

  /* Up is the next value, as on any spinbutton. Stopped here because the wheel
     sits in a modal whose own keys would otherwise see the press too. */
  const onKeyDown = (c: number) => (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    e.stopPropagation();
    cols.current[c]?.scrollBy({ top: e.key === 'ArrowUp' ? ROW : -ROW, behavior: behaviour() });
  };

  const pick = (c: number, row: number) => {
    if (drag.current?.moved) return;
    cols.current[c]?.scrollTo({ top: row * ROW, behavior: behaviour() });
  };

  const shown = indexesOf(value ?? FALLBACK, step);

  return (
    <div className={clsx(s.wheels, className)} role="group" aria-label={label ?? 'Time'}>
      {columns.map((col, c) => (
        <div
          key={col.label}
          ref={(el) => {
            cols.current[c] = el;
          }}
          className={s.wheel}
          tabIndex={0}
          role="spinbutton"
          aria-label={col.label}
          aria-valuetext={col.values[shown[c]]}
          onScroll={() => onScroll(c)}
          onTouchStart={() => {
            held.current = true;
          }}
          onTouchEnd={() => {
            held.current = false;
            wait(c);
          }}
          onTouchCancel={() => {
            held.current = false;
            wait(c);
          }}
          onPointerDown={startDrag(c)}
          onKeyDown={onKeyDown(c)}
        >
          {Array.from({ length: col.loop ? COPIES : 1 }, (_, copy) =>
            col.values.map((v, i) => {
              const row = copy * col.values.length + i;
              return (
                <div key={row} className={s.wheelItem} aria-hidden onClick={() => pick(c, row)}>
                  {v}
                </div>
              );
            }),
          )}
        </div>
      ))}
    </div>
  );
};
